// ════════════════════════════════════════════════════════════════════
// JARVIS BOOK OF BUSINESS PARSER  (UnitedHealthcare · Golden Rule)
//
// Jarvis is UHC's agent portal. Its "Book of Business" export gives
// the agent a CSV of every active / pending / terminated member
// they're the writing agent of record on — across IFP (ACA), Medicare
// Advantage, Med Supp, PDP, and the ancillary lines. We import that
// CSV straight into health_policies so the Health Portal renewal
// engine, MRR rollup, and disenrollment detector all light up
// without manual data entry.
//
// ──────────────────────────────────────────────────────────────────
// FILE LAYOUT  (verified against a real Jarvis export, June 2026)
// ──────────────────────────────────────────────────────────────────
//
// Row 1: blank line (CSV padded out to ~85 columns of trailing commas)
// Row 2: confidentiality banner — "Note: Book of Business Reports
//        are confidential and proprietary information of UnitedHealth
//        Group. Do not distribute..."
// Row 3: HEADER ROW. 17 named columns followed by trailing commas:
//        agentId, agentEmail, agentName, agentIdStatus, agentNpn,
//        memberFirstName, memberLastName, memberAddress1,
//        memberAddress2, memberCity, memberZip, memberState,
//        dateOfBirth, memberPhone, product, planStatus, Plan Status(MS)
// Row 4+: data rows. One row PER COVERED MEMBER (a household policy
//         with mom + 3 kids shows up as 4 rows sharing address +
//         phone). UHC pays PMPM per member, so each row is a
//         billable unit and gets its own health_policies entry.
//
// ──────────────────────────────────────────────────────────────────
// FIELD MAPPING  (Jarvis column → health_policies column)
// ──────────────────────────────────────────────────────────────────
//
//   memberFirstName + memberLastName → client_name  (TitleCased)
//   memberPhone                       → client_phone (10-digit normalized)
//   dateOfBirth                       → client_dob (MM/DD/YYYY → ISO)
//                                       client_age (derived)
//   product code                      → plan_type (see PRODUCT_MAP)
//   plan_name                         = product label (e.g.
//                                       "ACA Marketplace (UHC IFP)")
//   carrier                           = "UHC / Golden Rule"
//   planStatus (or Plan Status(MS))   → status (see STATUS_MAP)
//   agentId                           → agent_prod_num
//   lead_source                       = "Jarvis · UHC"
//   notes                             = address dump so the agent
//                                       can pull a mailer list later
//                                       without re-importing
//
// NOT in Jarvis: member_id, monthly_premium, monthly_commission,
//                effective_date, renewal_date, subsidy_amount.
// Those come from elsewhere — the commission statement parser
// (HCSC) or the agent's own data entry. We leave them null.
//
// ──────────────────────────────────────────────────────────────────
// DETECTION SIGNATURE
// ──────────────────────────────────────────────────────────────────
//
// A file is Jarvis if EITHER:
//   (a) it contains the confidentiality banner, OR
//   (b) its header row has the unique trio "agentId, agentNpn,
//       memberFirstName" together.
// Banner-only check would miss exports stripped of the header band;
// header-only check would miss exports where the column order is
// shuffled. Both signals together cover both cases.
// ════════════════════════════════════════════════════════════════════

const JARVIS_BANNER = /Book of Business Reports are confidential/i;

const PRODUCT_MAP = {
  IFP:     'aca_marketplace',     // Individual & Family Plans = UHC's ACA brand
  MS:      'medicare_supplement', // Medigap
  MA:      'medicare_advantage',
  MAPD:    'medicare_advantage',  // MA with Part D bundled
  PDP:     'medicare_pdp',
  STM:     'short_term',
  SAVERS:  'short_term',
  DV:      'dental_vision',
  DENTAL:  'dental_vision',
  HOSPITAL:'hospital_indemnity',
  HI:      'hospital_indemnity',
};

const STATUS_MAP = {
  A: 'active',
  P: 'pending',
  T: 'cancelled',    // terminated
  C: 'cancelled',
  L: 'lapsed',
  R: 'renewed',
};

const PRODUCT_LABEL = {
  aca_marketplace:    'ACA Marketplace (UHC IFP)',
  medicare_supplement:'Medicare Supplement (UHC)',
  medicare_advantage: 'Medicare Advantage (UHC)',
  medicare_pdp:       'Medicare Part D (UHC)',
  short_term:         'Short-Term Medical (UHC / Golden Rule)',
  dental_vision:      'Dental / Vision (UHC)',
  hospital_indemnity: 'Hospital Indemnity (UHC / Golden Rule)',
  other:              'UHC Other',
};

// Same CSV parser shape used in CommissionTracker.jsx — quoted fields,
// embedded commas, doubled "" escapes. Returns a string array.
function parseCsvLine(line) {
  const out = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') inQ = false;
      else cur += ch;
    } else {
      if (ch === ',')      { out.push(cur); cur = ''; }
      else if (ch === '\t'){ out.push(cur); cur = ''; }
      else if (ch === '"') inQ = true;
      else cur += ch;
    }
  }
  out.push(cur);
  return out.map(s => s.trim());
}

export function isJarvisFormat(text) {
  const head = String(text || '').split(/\r?\n/, 6).join('\n');
  if (JARVIS_BANNER.test(head)) return true;
  // Header-trio fallback — the three names below only co-occur in Jarvis.
  return /agentId/.test(head) && /agentNpn/.test(head) && /memberFirstName/.test(head);
}

function titleCase(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/\b([a-z])/g, (m, c) => c.toUpperCase());
}

// MM/DD/YYYY → ISO YYYY-MM-DD. Returns null on bad input.
function parseDob(mmddyyyy) {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(mmddyyyy || '').trim());
  if (!m) return null;
  const mm = String(m[1]).padStart(2, '0');
  const dd = String(m[2]).padStart(2, '0');
  return `${m[3]}-${mm}-${dd}`;
}

function ageFromDob(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00Z');
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let yrs = now.getUTCFullYear() - d.getUTCFullYear();
  const m = now.getUTCMonth() - d.getUTCMonth();
  if (m < 0 || (m === 0 && now.getUTCDate() < d.getUTCDate())) yrs--;
  return yrs >= 0 && yrs < 130 ? yrs : null;
}

function normalizePhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 10) return digits;
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  return digits || null;
}

// Walks the CSV, locates the header row (line that contains "agentId"
// and "memberFirstName"), then converts each subsequent data row to a
// normalized object. Banner / blank rows are skipped automatically.
// Returns { rows, header, agent, summary }.
export function parseJarvisCsv(text) {
  const lines = String(text || '').split(/\r?\n/);
  let headerIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (/agentId/i.test(lines[i]) && /memberFirstName/i.test(lines[i])) {
      headerIdx = i; break;
    }
  }
  if (headerIdx < 0) return { rows: [], header: null, agent: null, summary: empty() };

  const header = parseCsvLine(lines[headerIdx]);
  const col = {};
  header.forEach((h, i) => { col[h] = i; });

  // Locate the columns we care about with case-tolerant lookup.
  const idx = (...aliases) => {
    for (const a of aliases) {
      const k = Object.keys(col).find(c => c.toLowerCase() === a.toLowerCase());
      if (k != null) return col[k];
    }
    return -1;
  };
  const C = {
    agentId:          idx('agentId'),
    agentName:        idx('agentName'),
    agentEmail:       idx('agentEmail'),
    agentNpn:         idx('agentNpn'),
    firstName:        idx('memberFirstName'),
    lastName:         idx('memberLastName'),
    address1:         idx('memberAddress1'),
    address2:         idx('memberAddress2'),
    city:             idx('memberCity'),
    zip:              idx('memberZip'),
    state:            idx('memberState'),
    dob:              idx('dateOfBirth'),
    phone:            idx('memberPhone'),
    product:          idx('product'),
    planStatus:       idx('planStatus'),
    planStatusMs:     idx('Plan Status(MS)'),
  };

  const rows = [];
  let agentMeta = null;

  for (let i = headerIdx + 1; i < lines.length; i++) {
    const raw = lines[i];
    if (!raw || !raw.trim()) continue;
    const cells = parseCsvLine(raw);
    const get = (k) => (C[k] >= 0 ? (cells[C[k]] || '').trim() : '');

    const firstName = get('firstName');
    const lastName  = get('lastName');
    if (!firstName && !lastName) continue;

    // Capture agent metadata once (it repeats on every row in Jarvis).
    if (!agentMeta && get('agentId')) {
      agentMeta = {
        agentId:    get('agentId'),
        agentName:  get('agentName'),
        agentEmail: get('agentEmail'),
        agentNpn:   get('agentNpn'),
      };
    }

    const productRaw = get('product').toUpperCase();
    const plan_type  = PRODUCT_MAP[productRaw] || 'other';

    // Status: Med Supp uses Plan Status(MS); everything else uses
    // planStatus. Jarvis fills exactly one of the two per row.
    const statusRaw  = (get('planStatusMs') || get('planStatus') || '').toUpperCase();
    const status     = STATUS_MAP[statusRaw] || 'active';

    const dobIso  = parseDob(get('dob'));
    const phone10 = normalizePhone(get('phone'));

    rows.push({
      // ── source fields (kept for audit) ──
      _source: 'jarvis',
      _productRaw: productRaw,
      _statusRaw: statusRaw,

      // ── health_policies upsert payload ──
      client_name:    titleCase(`${firstName} ${lastName}`).trim(),
      client_phone:   phone10,
      client_email:   null,
      client_dob:     dobIso,
      client_age:     ageFromDob(dobIso),
      // Address goes in the structured columns (migration 044),
      // never in notes. Notes is for narrative only.
      client_address:  get('address1') || null,
      client_address2: get('address2') || null,
      client_city:     get('city')     || null,
      client_state:    get('state')    || null,
      client_zip:      get('zip')      || null,
      carrier:        'UHC / Golden Rule',
      plan_name:      PRODUCT_LABEL[plan_type] || PRODUCT_LABEL.other,
      plan_type,
      member_id:      null,         // Jarvis doesn't expose member ID
      monthly_premium:    null,     // not in this export
      monthly_commission: null,     // not in this export
      subsidy_amount:     null,     // APTC tracked separately
      effective_date:     null,     // not in this export
      renewal_date:       null,
      termination_date:   null,
      status,
      lead_source:    'Jarvis · UHC',
      notes:          null,
      agent_prod_num: get('agentId') || null,

      // ── address kept as a nested object too for legacy consumers ──
      _address: {
        address1: get('address1'),
        address2: get('address2'),
        city:     get('city'),
        state:    get('state'),
        zip:      get('zip'),
      },
    });
  }

  // Summary buckets — feeds the preview chip + the "what got
  // imported" toast in the UI.
  const summary = rows.reduce((acc, r) => {
    acc.total++;
    acc.byStatus[r.status]   = (acc.byStatus[r.status]   || 0) + 1;
    acc.byPlanType[r.plan_type] = (acc.byPlanType[r.plan_type] || 0) + 1;
    return acc;
  }, empty());

  return { rows, header, agent: agentMeta, summary };
}

function empty() {
  return { total: 0, byStatus: {}, byPlanType: {} };
}

// Bridge into the existing import flow: returns the exact upsert
// payload shape HealthImportCard already uses for HCSC + generic
// CSV (matching health_policies columns one-to-one).
export function jarvisRowToHealthPolicyUpsert(row, opts = {}) {
  const { ownerId, ownerProdNum } = opts;
  return {
    owner_id:           ownerId || null,
    client_name:        row.client_name,
    client_phone:       row.client_phone,
    client_email:       row.client_email,
    client_dob:         row.client_dob,
    client_age:         row.client_age,
    carrier:            row.carrier,
    plan_name:          row.plan_name,
    plan_type:          row.plan_type,
    member_id:          row.member_id,
    monthly_premium:    row.monthly_premium,
    monthly_commission: row.monthly_commission,
    subsidy_amount:     row.subsidy_amount,
    effective_date:     row.effective_date,
    renewal_date:       row.renewal_date,
    termination_date:   row.termination_date,
    status:             row.status,
    lead_source:        row.lead_source,
    notes:              row.notes,
    agent_prod_num:     row.agent_prod_num || ownerProdNum || null,
  };
}
